import { useQuery } from '@tanstack/react-query';
import { Select, Spin, Switch } from 'antd';
import { Field, FieldProps, Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { Button, Upload } from '../../../custom';
import {
  AdminScholarshipConfig,
  useGetAdminScholarshipConfigQuery,
} from '../../../hooks/api/scholarship/useGetAdminScholarshipConfigQuery';
import {
  ConfigureScholarshipPayload,
  useConfigureScholarshipMutation,
} from '../../../hooks/api/scholarship/useConfigureScholarshipMutation';
import { useUploadScholarshipTemplateMutation } from '../../../hooks/api/scholarship/useUploadScholarshipTemplateMutation';
import { useUploadScholarshipAcceptanceTemplateMutation } from '../../../hooks/api/scholarship/useUploadScholarshipAcceptanceTemplateMutation';
import { getRoles } from '../../userManagement/request';
import styles from '../schoolConfig/schoolConfig.module.scss';

interface SelectFieldProps {
  label: string;
  name: keyof ScholarshipConfigFormValues;
  loading?: boolean;
  mode?: 'multiple' | 'tags';
  options: { label: string; value: string | number }[];
  placeholder: string;
}

interface ReadOnlyFieldProps {
  label: string;
  value?: string;
}

type ScholarshipConfigFormValues = ConfigureScholarshipPayload;

interface ScholarshipTemplateFormValues {
  file: File | null;
}

const defaultScholarshipConfig: ScholarshipConfigFormValues = {
  isEnabled: false,
  eligibilityTrigger: 0,
  allowedFileExtensions: '',
  notificationRoles: '',
};

const eligibilityTriggerOptions = [
  { label: 'None', value: 0 },
  { label: 'After Acceptance Fee Payment', value: 1 },
  { label: 'After Application Fee Payment', value: 2 },
];

const fileExtensionOptions = [
  { label: 'PDF', value: 'pdf' },
  { label: 'JPG', value: 'jpg' },
  { label: 'JPEG', value: 'jpeg' },
  { label: 'PNG', value: 'png' },
  { label: 'DOC', value: 'doc' },
  { label: 'DOCX', value: 'docx' },
];

const validationSchema = Yup.object().shape({
  eligibilityTrigger: Yup.number().required('Eligibility trigger is required'),
  allowedFileExtensions: Yup.string().required('Allowed file extensions are required'),
  notificationRoles: Yup.string().required('Notification roles are required'),
});

const templateValidationSchema = Yup.object().shape({
  file: Yup.mixed().required('Template file is required'),
});

const splitCommaValue = (value?: string) => {
  if (!value || value === 'null') {
    return [];
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const SwitchField = ({ label, name }: { label: string; name: keyof ScholarshipConfigFormValues }) => {
  const { setFieldValue, values } = useFormikContext<ScholarshipConfigFormValues>();

  return (
    <div className={styles.switchField}>
      <label>{label}</label>
      <div className={styles.control}>
        <Switch checked={Boolean(values[name])} onChange={(checked) => setFieldValue(name, checked)} />
      </div>
    </div>
  );
};

const SelectField = ({ label, name, options, placeholder, loading, mode }: SelectFieldProps) => {
  const { setFieldValue, values } = useFormikContext<ScholarshipConfigFormValues>();
  const value = values[name];
  const isMultiValue = mode === 'multiple' || mode === 'tags';

  return (
    <Field name={name}>
      {({ meta }: FieldProps) => (
        <div className={styles.selectField}>
          <label>{label}</label>
          <div className={styles.control}>
            <Select
              allowClear
              loading={loading}
              mode={mode}
              options={options}
              placeholder={placeholder}
              size="large"
              style={{ width: '100%' }}
              value={isMultiValue ? splitCommaValue(String(value || '')) : value || undefined}
              onChange={(nextValue) => {
                setFieldValue(name, Array.isArray(nextValue) ? nextValue.join(',') : nextValue ?? '');
              }}
            />
            {meta.touched && meta.error && <div className={styles.error}>{meta.error}</div>}
          </div>
        </div>
      )}
    </Field>
  );
};

const ReadOnlyField = ({ label, value }: ReadOnlyFieldProps) => {
  return (
    <div className={styles.field}>
      <label>{label}</label>
      <div className={styles.control}>
        <input className={styles.numberInput} readOnly value={value || 'Not set'} />
      </div>
    </div>
  );
};

const getInitialValues = (config?: AdminScholarshipConfig): ScholarshipConfigFormValues => ({
  ...defaultScholarshipConfig,
  isEnabled: Boolean(config?.isEnabled),
  eligibilityTrigger: Number(config?.eligibilityTrigger ?? 0),
  allowedFileExtensions: config?.allowedFileExtensions === 'null' ? '' : config?.allowedFileExtensions ?? '',
  notificationRoles: config?.notificationRoles === 'null' ? '' : config?.notificationRoles ?? '',
});

const ScholarshipConfig = () => {
  const { scholarshipConfigData, isLoading, isError, errorMessage } = useGetAdminScholarshipConfigQuery();
  const { configureScholarshipHandler, isConfigureScholarshipLoading } = useConfigureScholarshipMutation();
  const { uploadScholarshipTemplateHandler, isUploadScholarshipTemplateLoading } =
    useUploadScholarshipTemplateMutation();
  const { uploadScholarshipAcceptanceTemplateHandler, isUploadScholarshipAcceptanceTemplateLoading } =
    useUploadScholarshipAcceptanceTemplateMutation();

  const { data: roles, isLoading: isRolesLoading } = useQuery({
    queryKey: ['get-roles'],
    queryFn: getRoles,
  });

  const roleOptions = roles?.map((role) => ({ label: role.name, value: role.name })) ?? [];

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>Error: {errorMessage}</div>;
  }

  return (
    <main>
      <section className="space-between">
        <h3>Scholarship Configuration</h3>
      </section>

      <section className={styles.formCard}>
        <Formik
          initialValues={getInitialValues(scholarshipConfigData)}
          onSubmit={(values, { resetForm }) => configureScholarshipHandler(values, resetForm)}
          validationSchema={validationSchema}
          enableReinitialize
        >
          <Form>
            <div className={styles.section}>
              <h4>Availability</h4>
              <div className={styles.grid}>
                <SwitchField label="Enable Scholarship Applications" name="isEnabled" />
                <SelectField
                  label="Eligibility Trigger"
                  name="eligibilityTrigger"
                  options={eligibilityTriggerOptions}
                  placeholder="Select eligibility trigger"
                />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Submission Rules</h4>
              <div className={styles.grid}>
                <SelectField
                  label="Allowed File Extensions"
                  name="allowedFileExtensions"
                  options={fileExtensionOptions}
                  placeholder="Select or type extensions"
                  mode="tags"
                />
                <SelectField
                  label="Notification Roles"
                  name="notificationRoles"
                  options={roleOptions}
                  placeholder="Select roles to notify"
                  loading={isRolesLoading}
                  mode="multiple"
                />
              </div>
            </div>

            <div className={styles.section}>
              <h4>Current Template</h4>
              <div className={styles.grid}>
                <ReadOnlyField label="Original File Name" value={scholarshipConfigData?.templateOriginalName} />
                <ReadOnlyField label="Stored File Name" value={scholarshipConfigData?.templateFileName} />
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                type="submit"
                disabled={isConfigureScholarshipLoading}
                isLoading={isConfigureScholarshipLoading}
                text={isConfigureScholarshipLoading ? 'Saving...' : 'Save Changes'}
              />
            </div>
          </Form>
        </Formik>
      </section>

      <section className={styles.formCard}>
        <Formik<ScholarshipTemplateFormValues>
          initialValues={{ file: null }}
          onSubmit={(values, { resetForm }) => uploadScholarshipTemplateHandler(values, resetForm)}
          validationSchema={templateValidationSchema}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <div className={styles.section}>
                <h4>Upload Scholarship Application Template</h4>
                <Upload
                  name="file"
                  label="Application File"
                  fileName={values.file?.name}
                  allowedFormats={splitCommaValue(scholarshipConfigData?.allowedFileExtensions).map((item) =>
                    item.toUpperCase(),
                  )}
                  accept={splitCommaValue(scholarshipConfigData?.allowedFileExtensions)
                    .map((item) => `.${item}`)
                    .join(',')}
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    setFieldValue('file', file);
                  }}
                />
              </div>

              <div className={styles.actions}>
                <Button
                  type="submit"
                  disabled={isUploadScholarshipTemplateLoading}
                  isLoading={isUploadScholarshipTemplateLoading}
                  text={isUploadScholarshipTemplateLoading ? 'Uploading...' : 'Upload Template'}
                />
              </div>
            </Form>
          )}
        </Formik>
      </section>

      <section className={styles.formCard}>
        <Formik<ScholarshipTemplateFormValues>
          initialValues={{ file: null }}
          onSubmit={(values, { resetForm }) => uploadScholarshipAcceptanceTemplateHandler(values, resetForm)}
          validationSchema={templateValidationSchema}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <div className={styles.section}>
                <h4>Upload Scholarship Acceptance Template</h4>
                <Upload
                  name="file"
                  label="Acceptance File"
                  fileName={values.file?.name}
                  allowedFormats={splitCommaValue(scholarshipConfigData?.acceptanceAllowedFileExtensions).map((item) =>
                    item.toUpperCase(),
                  )}
                  accept={splitCommaValue(scholarshipConfigData?.acceptanceAllowedFileExtensions)
                    .map((item) => `.${item}`)
                    .join(',')}
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    setFieldValue('file', file);
                  }}
                />
              </div>

              <div className={styles.actions}>
                <Button
                  type="submit"
                  disabled={isUploadScholarshipAcceptanceTemplateLoading}
                  isLoading={isUploadScholarshipAcceptanceTemplateLoading}
                  text={isUploadScholarshipAcceptanceTemplateLoading ? 'Uploading...' : 'Upload Template'}
                />
              </div>
            </Form>
          )}
        </Formik>
      </section>
    </main>
  );
};

export default ScholarshipConfig;
